import type { StandardCostPlan, PlanStage, PlanStageIssue } from '@/api/productionOrder.api';
import { money } from '@/pages/masters/routing/stageCost';

// Routing of the order for its quantity, stage by stage — work center, machine, crew, the time the
// quantity takes and what it costs — with the standard cost of the whole order. The figures come
// from the backend's standard cost plan: the same plan is saved on the order when it is confirmed,
// its work orders are planned for the same minutes, and its Costing tab shows the same standard.

interface Props {
    plan: StandardCostPlan | null;
    loading: boolean;
    error?: string | null;
    hasRouting: boolean;
    unit: string;
}

function formatMinutes(total: number) {
    const rounded = Math.round(total);
    const hrs = Math.floor(rounded / 60);
    const mins = rounded % 60;
    if (hrs === 0) return `${mins} min`;
    if (mins === 0) return `${hrs} hr${hrs === 1 ? '' : 's'}`;
    return `${hrs} hr ${mins} min`;
}

const ISSUE_TEXT: Record<PlanStageIssue, string> = {
    NO_WORK_CENTER: 'No work center: labour costed at the average operator wage',
    NO_MACHINE: 'Machine work center without a machine: no machine cost counted',
    NO_LABOUR_RATE: 'Work center has no labour rate: labour costs ₹0',
    NO_MACHINE_RATE: 'Machine has no hour rate: machine costs ₹0',
    NO_JOB_RATE: 'No job rate set: job work costs ₹0',
};

const WC_TYPE_LABEL: Record<string, string> = { MANUAL: 'Labour', LABOR: 'Labour', MACHINE: 'Machine', BOTH: 'Machine + Labour' };

function timeBreakdown(st: PlanStage, unit: string) {
    const run = st.basis === 'PER_BATCH'
        ? `${st.runMinutes} min × ${st.runs} batch${st.runs === 1 ? '' : 'es'} of ${st.batchSize}`
        : `${st.runMinutes} min × ${st.runs} ${unit}`;
    return st.setupMinutes > 0 ? `setup ${st.setupMinutes} + ${run}` : run;
}

export default function RoutingCostPlan({ plan, loading, error, hasRouting, unit }: Props) {
    const t = plan?.totals;
    const unitLabel = unit || 'Pcs';

    return (
        <div className="bg-white border border-slate-200/90 rounded-2xl p-5 mb-5 shadow-xs">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <i className="ri-route-line text-indigo-600 text-base" />
                    <div>
                        <h3 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight">
                            Routing &amp; Standard Cost for {plan?.qty ?? 0} {unitLabel}
                        </h3>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                            Saved on the order when it is confirmed; its work orders and Costing tab use the same figures
                        </p>
                    </div>
                </div>
                {loading && (
                    <span className="text-xs text-indigo-600 flex items-center gap-1">
                        <i className="ri-loader-4-line animate-spin" /> Calculating…
                    </span>
                )}
            </div>

            {error && (
                <div className="mb-3 p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
                    <i className="ri-error-warning-line" /> {error}
                </div>
            )}

            {/* Stage by stage */}
            {!hasRouting ? (
                <p className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-xl px-3 py-4 text-center mb-4">
                    No routing selected: only material and the overhead % are costed, and no work orders are raised.
                </p>
            ) : plan && plan.stages.length === 0 ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-3 mb-4">
                    This routing has no stages.
                </p>
            ) : plan ? (
                <div className="overflow-x-auto mb-4 border border-slate-100 rounded-xl">
                    <table className="w-full min-w-max text-xs">
                        <thead>
                            <tr className="bg-slate-50 text-slate-500">
                                {['#', 'Stage', 'Work Center / Machine', 'Crew', `Time for ${plan.qty} ${unitLabel}`, 'QC', 'Labour', 'Machine', 'Stage Cost'].map((h) => (
                                    <th key={h} className={`px-3 py-2.5 font-semibold uppercase tracking-wide whitespace-nowrap ${['Labour', 'Machine', 'Stage Cost'].includes(h) ? 'text-right' : 'text-left'}`}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {plan.stages.map((st) => {
                                const jobWork = st.stageType === 'JOB_WORK';
                                return (
                                    <tr key={st.stageNumber} className="align-top">
                                        <td className="px-3 py-2.5">
                                            <span className="w-6 h-6 inline-flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 font-bold">{st.stageNumber}</span>
                                        </td>
                                        <td className="px-3 py-2.5 min-w-[150px]">
                                            <p className="font-semibold text-slate-800">{st.stageName}</p>
                                            {jobWork && (
                                                <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-semibold">
                                                    <i className="ri-truck-line" /> Job work{st.subcontractorName ? ` · ${st.subcontractorName}` : ''}
                                                </span>
                                            )}
                                            {st.issues.map((code) => (
                                                <p key={code} className="text-[11px] text-amber-600 mt-0.5"><i className="ri-error-warning-line" /> {ISSUE_TEXT[code]}</p>
                                            ))}
                                        </td>
                                        <td className="px-3 py-2.5 whitespace-nowrap">
                                            {jobWork ? <span className="text-slate-400">At sub-contractor</span> : (
                                                <>
                                                    <p className="font-medium text-slate-800">
                                                        {st.workCenter?.name || <span className="text-rose-600">Not assigned</span>}
                                                        {st.workCenter?.type && <span className="font-normal text-slate-400"> · {WC_TYPE_LABEL[String(st.workCenter.type).toUpperCase()] || st.workCenter.type}</span>}
                                                    </p>
                                                    <p className="text-slate-500">
                                                        {st.machine ? [st.machine.code, st.machine.name].filter(Boolean).join(' · ') : 'Manual work'}
                                                    </p>
                                                </>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{jobWork ? '—' : st.crew}</td>
                                        <td className="px-3 py-2.5 whitespace-nowrap">
                                            {jobWork ? <span className="text-slate-400">Sub-contractor's time</span> : (
                                                <>
                                                    <p className="font-semibold text-slate-800">{formatMinutes(st.plannedMinutes)}</p>
                                                    <p className="text-slate-400">{timeBreakdown(st, unitLabel)}</p>
                                                    {st.crew > 1 && <p className="text-slate-400">{st.labourHours} person-hrs</p>}
                                                </>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 whitespace-nowrap">
                                            {st.qcRequired ? (
                                                <>
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                                                        <i className="ri-shield-check-line" /> QC
                                                    </span>
                                                    {st.qcChecklistName && <p className="text-slate-500 mt-0.5">{st.qcChecklistName}</p>}
                                                </>
                                            ) : <span className="text-slate-400">—</span>}
                                        </td>
                                        <td className="px-3 py-2.5 whitespace-nowrap text-right">
                                            {jobWork ? <span className="text-slate-400">—</span> : (
                                                <>
                                                    <p className="font-medium text-slate-800">{money(st.labourCost)}</p>
                                                    <p className="text-slate-400">{money(st.labourPerHour)}/hr</p>
                                                </>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 whitespace-nowrap text-right">
                                            {jobWork || !st.machine ? <span className="text-slate-400">—</span> : (
                                                <>
                                                    <p className="font-medium text-slate-800">{money(st.machineCost)}</p>
                                                    <p className="text-slate-400">{money(st.machinePerHour)}/hr</p>
                                                </>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 whitespace-nowrap text-right">
                                            <p className="font-semibold text-slate-900">{money(st.cost)}</p>
                                            {jobWork && <p className="text-slate-400">{money(st.jobRate)} × {plan.qty}</p>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {t && (
                            <tfoot>
                                <tr className="bg-slate-50 font-semibold text-slate-800 border-t border-slate-200">
                                    <td className="px-3 py-2.5" colSpan={4}>
                                        {plan.stages.length} stage{plan.stages.length === 1 ? '' : 's'}{t.qcStages > 0 ? ` · ${t.qcStages} with QC` : ''}
                                    </td>
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        {formatMinutes(t.plannedMinutes)}
                                        <span className="block font-normal text-slate-400">{t.labourHours} person-hrs</span>
                                    </td>
                                    <td className="px-3 py-2.5" />
                                    <td className="px-3 py-2.5 text-right whitespace-nowrap">{money(t.inHouseLabour)}</td>
                                    <td className="px-3 py-2.5 text-right whitespace-nowrap">{money(t.machine)}</td>
                                    <td className="px-3 py-2.5 text-right whitespace-nowrap">{money(t.inHouseLabour + t.machine + t.jobWork)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            ) : null}

            {/* Standard cost of the order */}
            {t && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
                    {[
                        { label: 'Material', value: t.material, hint: `${plan!.materials.length} BOM line${plan!.materials.length === 1 ? '' : 's'}` },
                        { label: 'Labour', value: t.inHouseLabour, hint: `${t.labourHours} person-hrs` },
                        { label: 'Job Work', value: t.jobWork, hint: 'sub-contractor charges' },
                        { label: 'Machine', value: t.machine, hint: 'hour rate, in overhead' },
                        {
                            label: `Overhead ${t.overheadPercent > 0 ? `${plan!.overheadPct}%` : ''}`.trim(),
                            value: t.overheadPercent,
                            hint: t.overheadPercent > 0 ? 'of material + labour' : 'machine hour method',
                        },
                        { label: 'Total Standard Cost', value: t.total, hint: `${plan!.qty} ${unitLabel}`, strong: true },
                        { label: 'Per Unit', value: t.perUnit, hint: `per ${unitLabel}`, strong: true },
                    ].map((c) => (
                        <div key={c.label} className={`rounded-xl border px-3 py-2.5 ${c.strong ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200'}`}>
                            <p className={`text-[11px] font-semibold uppercase tracking-wide ${c.strong ? 'text-indigo-700' : 'text-slate-500'}`}>{c.label}</p>
                            <p className={`mt-0.5 font-bold ${c.strong ? 'text-indigo-900 text-base' : 'text-slate-800 text-sm'}`}>{money(c.value)}</p>
                            <p className="text-[11px] text-slate-400">{c.hint}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
